import type { ConnectorView, EnrollmentIssued } from '@tabel/shared/schemas/agent-client';
import { useQuery } from '@tanstack/react-query';
import { Check, CircleCheck, Copy, LoaderCircle, TriangleAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { errorSentence } from '../../components/ErrorToast';
import { Button, Callout, Modal, Segmented, Skeleton, fieldCls } from '../../components/ui';
import { connectorsKey, issueEnrollment, listConnectors } from '../../lib/agent-api';
import { useAgentSocket } from '../../lib/agent-ws';
import { reportClientError } from '../../lib/errors';
import { useRole } from '../../lib/role';
import { ErrorCode, shouldReport } from './parts';
import { OS_LABEL, PC_LABEL, PC_T, PC_TITLE, pcSay } from './texts';

/*
 * Окно «Подключить компьютер» (006, T041; US1; FR-001; contracts/ui.md §1, мокап Clients.dc.html).
 * - При открытии — ключ подключения (`POST /api/agent/connectors/enrollments`): одна команда установки для macOS и
 *   Linux, установщик сам узнаёт систему. Переключатель macOS / Linux меняет только пояснение о службе.
 * - Ключ скрыт: на экране — `commandMasked`; «Скопировать» кладёт в буфер обмена `command` целиком. Ключ живёт только
 *   в памяти окна: его нет в адресе страницы, в хранилище браузера, в кэше запросов и в журнале. Окно закрыто, срок
 *   истёк или компьютер подключился — ключ забыт. Буфер обмена недоступен — команда целиком в поле только для чтения:
 *   её копируют вручную.
 * - Обратный отсчёт «Ключ действует ещё 14:59 и только один раз» — каждую секунду; срок истёк — «Получить новый ключ».
 * - «Ждём компьютер…» до сообщения WS `connector` о новом компьютере в сети; затем список компьютеров заново и
 *   «Компьютер <имя> в сети». Новый — компьютер, которого не было в списке, когда выдан ключ. Если WebSocket не
 *   работает, список заново раз в 5 с — окно не зависнет в ожидании.
 * - Подвал: «Отмена» и главная «Перейти к агентам» — она доступна, когда компьютер в сети.
 * - Ключ выдаётся один раз на открытие: лимит — 5 ключей в час (contracts/http-api.md, «Лимиты частоты»).
 */

const SCREEN = 'MyAgents';
/** Список заново, если сообщение WS не пришло (первый компьютер: опроса страницы ещё нет). */
const FALLBACK_MS = 5000;

type Os = keyof typeof OS_LABEL;
type Phase =
  | { kind: 'issuing' }
  | { kind: 'error'; error: unknown }
  | { kind: 'ready'; issued: EnrollmentIssued }
  | { kind: 'expired' }
  | { kind: 'connected'; pc: ConnectorView };

export function ConnectDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  /** «Перейти к агентам»: новый компьютер. */
  onDone: (connectorId: number) => void;
}) {
  const { role } = useRole();
  const list = useQuery({ queryKey: connectorsKey(role), queryFn: listConnectors, enabled: open });
  const [phase, setPhase] = useState<Phase | null>(null);
  const [os, setOs] = useState<Os>('macos');
  const [now, setNow] = useState(() => Date.now());
  /** Компьютеры, которые были, когда выдан ключ. */
  const known = useRef<Set<number> | null>(null);
  /** Список тогда загрузился: любой другой компьютер в сети — новый. Иначе новый — только по сообщению WS. */
  const knownFull = useRef(false);
  /** Новые компьютеры по сообщениям WS. */
  const candidates = useRef(new Set<number>());
  /** Ключ уже запрошен для этого открытия (StrictMode запускает эффекты дважды). */
  const requested = useRef(false);
  const refetch = list.refetch;

  const issue = async () => {
    requested.current = true;
    setPhase({ kind: 'issuing' });
    known.current = new Set((list.data ?? []).map((c) => c.id));
    knownFull.current = list.data !== undefined;
    candidates.current = new Set();
    try {
      const issued = await issueEnrollment();
      setNow(Date.now());
      setPhase({ kind: 'ready', issued });
    } catch (error) {
      setPhase({ kind: 'error', error });
      if (shouldReport(error)) reportClientError(SCREEN, 'issue-enrollment', error);
    }
  };

  // Открыли — ключ, когда список компьютеров известен; закрыли — всё забыто.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ключ — один раз на открытие
  useEffect(() => {
    if (!open) {
      requested.current = false;
      known.current = null;
      setPhase(null);
      setOs('macos');
      return;
    }
    if (!requested.current && (list.isSuccess || list.isError)) void issue();
  }, [open, list.isSuccess, list.isError]);

  const waiting = phase?.kind === 'ready';

  // Обратный отсчёт и запасной опрос списка.
  useEffect(() => {
    if (!waiting) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(() => void refetch(), FALLBACK_MS);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [waiting, refetch]);

  // Срок истёк — ключ забыт.
  useEffect(() => {
    if (phase?.kind === 'ready' && Date.parse(phase.issued.expiresAt) <= now) setPhase({ kind: 'expired' });
  }, [phase, now]);

  // Новый компьютер в сети — по списку компьютеров.
  useEffect(() => {
    if (phase?.kind !== 'ready' && phase?.kind !== 'expired') return;
    const before = known.current;
    if (!before || !list.data) return;
    const fresh = list.data.find(
      (c) =>
        c.phase === 'active' &&
        c.online &&
        !before.has(c.id) &&
        (knownFull.current || candidates.current.has(c.id)),
    );
    if (fresh) setPhase({ kind: 'connected', pc: fresh });
  }, [phase, list.data]);

  useAgentSocket((e) => {
    if (e.op === 'connector') {
      if (e.online && known.current && !known.current.has(e.connectorId))
        candidates.current.add(e.connectorId);
      void refetch();
    } else if (e.op === 'open' && e.reconnected) void refetch();
  }, open);

  const connected = phase?.kind === 'connected' ? phase.pc : null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={PC_TITLE.connect}
      description={PC_T.connectIntro}
      width="w-[600px]"
    >
      <div className="space-y-4">
        {!connected && (
          <Segmented
            label={PC_LABEL.system}
            value={os}
            onChange={setOs}
            options={[
              { value: 'macos', label: OS_LABEL.macos },
              { value: 'linux', label: OS_LABEL.linux },
            ]}
          />
        )}
        {phase?.kind === 'ready' ? (
          <Ready issued={phase.issued} left={Date.parse(phase.issued.expiresAt) - now} os={os} />
        ) : phase?.kind === 'expired' ? (
          <div className="space-y-3">
            <Callout tone="warn" icon={TriangleAlert}>
              {PC_T.expired}
            </Callout>
            <Button onClick={() => void issue()}>{PC_LABEL.newKey}</Button>
          </div>
        ) : phase?.kind === 'error' ? (
          <div className="space-y-3">
            <Callout tone="bad" icon={TriangleAlert}>
              <p className="font-semibold text-ink">{PC_TITLE.keyError}</p>
              <p>{errorSentence(phase.error)}</p>
              <ErrorCode error={phase.error} />
            </Callout>
            <Button onClick={() => void issue()}>{PC_LABEL.retry}</Button>
          </div>
        ) : connected ? (
          <div role="status">
            <Callout tone="ok" icon={CircleCheck}>
              <b className="font-semibold">{pcSay.connected(connected.name)}</b> {PC_T.connected}
            </Callout>
          </div>
        ) : (
          <div aria-busy="true" aria-label={PC_LABEL.issuing}>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="mt-3 h-4 w-4/5" />
          </div>
        )}
        <div data-actions className="flex flex-wrap justify-end gap-2 pt-1 max-sm:[&>button]:flex-auto">
          <Button onClick={onClose}>{PC_LABEL.cancel}</Button>
          <Button variant="primary" disabled={!connected} onClick={() => connected && onDone(connected.id)}>
            {PC_LABEL.goToAgents}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

type CopyState = 'idle' | 'copied' | 'failed';

/** Команда со скрытым ключом, «Скопировать», срок ключа, пояснение о службе и «Ждём компьютер…». */
function Ready({ issued, left, os }: { issued: EnrollmentIssued; left: number; os: Os }) {
  const [copy, setCopy] = useState<CopyState>('idle');
  const field = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  useEffect(() => {
    if (copy === 'failed') {
      field.current?.focus();
      field.current?.select();
    }
  }, [copy]);
  const run = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('буфер обмена недоступен');
      await navigator.clipboard.writeText(issued.command);
      setCopy('copied');
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopy((c) => (c === 'copied' ? 'idle' : c)), 2000);
    } catch {
      setCopy('failed');
    }
  };
  const copied = copy === 'copied';
  const service = os === 'macos' ? PC_T.serviceMacos : PC_T.serviceLinux;
  return (
    <div className="space-y-4">
      {copy === 'failed' ? (
        <div>
          <input
            ref={field}
            readOnly
            aria-label={PC_LABEL.command}
            value={issued.command}
            spellCheck={false}
            onFocus={(e) => e.currentTarget.select()}
            className={`${fieldCls} w-full min-w-0 font-mono`}
          />
          <p className="mt-1.5 text-[12.5px] text-warn">{PC_T.copyFailed}</p>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-control border border-line bg-subtle py-1 pr-1 pl-3 max-sm:flex-col max-sm:items-stretch max-sm:py-2 max-sm:pr-2">
          <figure aria-label={PC_LABEL.command} className="m-0 min-w-0 flex-1">
            <code className="block font-mono text-[12px] leading-normal break-all whitespace-pre-wrap text-ink">
              {issued.commandMasked}
            </code>
          </figure>
          <Button icon={copied ? Check : Copy} onClick={() => void run()}>
            {copied ? PC_LABEL.copied : PC_LABEL.copy}
          </Button>
        </div>
      )}
      <p className="text-[13px] leading-normal text-ink-2 tabular-nums">
        {`${PC_T.keyHidden} ${pcSay.keyLeft(left)} ${service}`}
      </p>
      <div role="status" className="[&_svg]:animate-spin motion-reduce:[&_svg]:animate-none">
        <Callout tone="accent" icon={LoaderCircle}>
          {PC_T.waiting}
        </Callout>
      </div>
    </div>
  );
}
